import { useEffect, useState } from 'react';
import IconVariables from 'crono-fe-common/icons/Icon-Variables';
import styled from '@emotion/styled';
import { colors } from 'crono-fe-common/theme';
import SnippetModal from 'pages/settings/VariablesPage/SnippetModal';
import usePostSnippetVariable from 'hooks/services/variables/usePostSnippetVariable';
import { useConditionalSnackBar } from 'context/snackbar';
import { getError } from 'crono-fe-common/utils';

interface IProps {
  positionCreateSnippet: {
    bottom: number;
    left: number;
  } | null;
  editorHeight: number;
  editorWidth: number;
  handleReplaceSnippet?: (templateVariableName: string) => void;
  getInitialContent: () => string | undefined;
  bottomOffset?: number;
}

//For UX reasons we decided for now to put the element in the bottom right corner, but since it may be changing in the future, we are keeping the code here
const CreateSnippetButton = ({
  positionCreateSnippet,
  editorHeight,
  editorWidth,
  handleReplaceSnippet,
  getInitialContent,
  bottomOffset = 0,
}: IProps) => {
  const [localPositionCreateSnippet, setLocalPositionCreateSnippet] = useState<{
    bottom: number;
    left: number;
  } | null>(null);
  const [openSnippetCreation, setOpenSnippetCreation] =
    useState<boolean>(false);
  useEffect(() => {
    //We do not update the position if the modal is opened
    if (openSnippetCreation) return;
    setLocalPositionCreateSnippet(positionCreateSnippet);
  }, [positionCreateSnippet, openSnippetCreation]);

  const [initialContent, setInitialContent] = useState<string>('');

  const {
    mutateAsync: postSnippetVariableAsync,
    isSuccess: isSuccessPostSnippetVariable,
    error: errorPostSnippetVariable,
  } = usePostSnippetVariable();

  useEffect(() => {
    if (isSuccessPostSnippetVariable) {
      setOpenSnippetCreation(false);
    }
  }, [isSuccessPostSnippetVariable]);

  const onCreate = async (
    label: string,
    content: string,
    userBased: boolean,
  ) => {
    setOpenSnippetCreation(false);
    try {
      const res = await postSnippetVariableAsync({
        content,
        label,
        userBased,
      });
      if (res.data.data && handleReplaceSnippet) {
        handleReplaceSnippet(res.data.data.templateVariableName);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateSnippet = () => {
    const content = getInitialContent();
    if (!content) return;
    setInitialContent(content);
    setOpenSnippetCreation(true);
  };

  useConditionalSnackBar([
    {
      condition: !!isSuccessPostSnippetVariable,
      message: 'Snippet created successfully',
      severity: 'success',
    },
    {
      condition: !!errorPostSnippetVariable,
      message:
        getError(errorPostSnippetVariable) ?? 'Error while creating snippet',
      severity: 'error',
    },
  ]);
  //Disabled for the moment while we decide what to do with it
  return null;
  return (
    localPositionCreateSnippet && (
      <>
        {openSnippetCreation && (
          <SnippetModal
            onClose={() => setOpenSnippetCreation(false)}
            onCreate={onCreate}
            onUpdate={(_label: string, _content: string, _id: number) => {}}
            snippet={null}
            initialContent={initialContent}
          />
        )}
        <CreateSnippetButtonWrapper
          style={{
            // top: `min(${localPositionCreateSnippet.bottom + topOffset}px, ${editorHeight + topOffset}px)`,
            // left: `min(${localPositionCreateSnippet.left}px, ${editorWidth - 180}px)`,
            //For UX reasons we decided for now to put the element in the bottom right corner, but since it may be changing in the future, we are keeping the code here
            bottom: 5 + bottomOffset,
            right: 5,
          }}
          onClick={(ev) => {
            handleCreateSnippet();
            ev.stopPropagation();
            ev.preventDefault();
          }}
          id="create-snippet-button"
        >
          <div className="button-create-snippet">
            <IconVariables />
            Create snippet
          </div>
        </CreateSnippetButtonWrapper>
      </>
    )
  );
};

const CreateSnippetButtonWrapper = styled.div`
  z-index: 1000;
  background: ${colors.white};
  position: absolute;
  border-radius: 5px;
  border: 1px solid ${colors.grey444};
  padding: 4px;
  .button-create-snippet {
    background: ${colors.white};
    color: ${colors.black};
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 14px;
    line-height: 16px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
    :hover {
      background: ${colors.primaryLight};
      color: ${colors.primary};
      cursor: pointer;
      > svg > path {
        stroke: ${colors.primary};
      }
      > svg > circle {
        fill: ${colors.primary};
      }
    }
  }
`;

export default CreateSnippetButton;
